import React, { useState } from 'react';
import { carteraApi } from './api';
import { money } from './helpers';
import ModalPortal from './ModalPortal';

// ── Piezas compartidas con RecuperarAutoModal ──────────────────────────────
// Al recuperar y al cambiar el resultado después se piden las mismas cosas
// (forma, qué pasa con el faltante del titular y del aval, monto). Viven aquí
// a nivel de módulo (no dentro de un componente) para no duplicarlas.

// Fecha de hoy en la zona del navegador (la que ve quien captura), para los
// topes de los input date.
export const hoyLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Acepta "26,234", "$26,234.50" → número. Vacío → null (el valor es opcional).
export const parseMonto = (s) => {
  const limpio = String(s ?? '').replace(/[^0-9.]/g, '');
  if (!limpio) return null;
  const n = parseFloat(limpio);
  return Number.isFinite(n) ? n : null;
};

export const FORMAS = [
  ['entrega', 'Lo entregó el cliente'],
  ['juicio', 'Por juicio'],
];
export const FORMA_TXT = { entrega: 'Entregado por el cliente', juicio: 'Por juicio' };

const OPC_TITULAR = [['perdona', 'Perdonar'], ['cobra', 'Seguir cobrando']];
const OPC_AVAL = [['igual', 'Igual que el titular'], ['perdona', 'Perdonar'], ['cobra', 'Seguir cobrando']];

// El auto cubre la deuda cuando no falta nada (el back usa la misma tolerancia).
// faltante desconocido (null) NO cuenta como cubierto: se dejan elegir las opciones.
export const cubreDeuda = (faltante) => faltante != null && Number(faltante) <= 0.005;

// Estado inicial de la elección: titular perdona y el aval sigue al titular.
export const RESULTADO_INICIAL = { titular: 'perdona', aval: 'igual', monto: null };

// Hay saldo que se sigue cobrando si el titular cobra o el aval cobra por su
// cuenta. Un aval «igual» solo cobra cuando el titular cobra.
export const cobraAlguno = (v, tieneAval) => v.titular === 'cobra' || (!!tieneAval && v.aval === 'cobra');

// monto null = no lo ha tocado: se propone el faltante.
const textoMonto = (v, faltante) => (v.monto != null ? v.monto : (faltante != null ? Number(faltante).toFixed(2) : ''));

export const validarResultadoBuro = (v, tieneAval, faltante) => {
  if (cubreDeuda(faltante) || !cobraAlguno(v, tieneAval)) return null;
  return parseMonto(textoMonto(v, faltante)) > 0
    ? null
    : 'Indica el monto pendiente que se sigue cobrando (mayor a cero).';
};

// Cuando el auto cubre la deuda no se elige nada → no se manda resultado_buro.
export const armarResultadoBuro = (v, tieneAval, faltante) => {
  if (cubreDeuda(faltante)) return null;
  return {
    titular: v.titular,
    ...(tieneAval ? { aval: v.aval } : {}),
    monto_pendiente: cobraAlguno(v, tieneAval) ? parseMonto(textoMonto(v, faltante)) : null,
  };
};

// Elección inicial a partir del cierre guardado (resultado_buro puede ser null
// en recuperaciones viejas → valores por omisión).
const eleccionDeCierre = (rb) => {
  if (!rb) return { ...RESULTADO_INICIAL };
  const t = rb.titular || {};
  const a = rb.aval || {};
  const titular = t.resultado === 'cobra' ? 'cobra' : 'perdona';
  const aval = ['igual', 'perdona', 'cobra'].includes(a.resultado) ? a.resultado : 'igual';
  const monto = t.resultado === 'cobra' && t.monto_pendiente != null
    ? t.monto_pendiente
    : (a.resultado === 'cobra' && a.monto_pendiente != null ? a.monto_pendiente : null);
  return { titular, aval, monto: monto != null ? Number(monto).toFixed(2) : null };
};

const Fila = ({ label, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '3px 0', color: '#475569' }}>
    <span>{label}</span>
    <b style={{ color: '#0C2036', fontVariantNumeric: 'tabular-nums' }}>{children}</b>
  </div>
);

/** Grupo de radios en línea. */
export const Opciones = ({ nombre, etiqueta, valor, opciones, onChange, disabled }) => (
  <div role="radiogroup" aria-label={etiqueta || nombre} style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px' }}>
    {opciones.map(([v, t]) => (
      <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#0C2036', cursor: disabled ? 'default' : 'pointer' }}>
        <input type="radio" name={nombre} value={v} checked={valor === v} disabled={disabled}
          onChange={() => onChange(v)} />
        {t}
      </label>
    ))}
  </div>
);

/**
 * Bloque «Resultado para Buró»: deuda, valor del auto y faltante; si el auto
 * cubre no se elige nada; si no, el titular (y el aval, si hay) eligen perdonar
 * o seguir cobrando. `value` = { titular, aval, monto } (monto null → faltante).
 */
export const ResultadoBuroCampos = ({ deuda, valorAuto, faltante, tieneAval, avalNombre, value, onChange, disabled }) => {
  const cubre = cubreDeuda(faltante);
  const cobra = cobraAlguno(value, tieneAval);
  const set = (parte) => onChange({ ...value, ...parte });
  const dinero = (n) => (n != null ? money(n) : '—');

  return (
    <div style={{ background: '#F6F9FB', border: '1px solid #E2E8F0', borderRadius: 12, padding: '12px 14px', marginTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#0C2036', marginBottom: 6 }}>Resultado para Buró</div>
      <Fila label="Deuda">{dinero(deuda)}</Fila>
      <Fila label="Valor del auto">{valorAuto != null ? money(valorAuto) : 'Sin capturar'}</Fila>
      <Fila label="Faltante">{dinero(faltante)}</Fila>

      {cubre ? (
        <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700, color: '#06794b' }}>
          <i className="pi pi-check-circle" style={{ marginRight: 6 }} />El auto cubre la deuda
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          <div className="cm-field">
            <label>Titular · lo que falta</label>
            <Opciones nombre="resultado-titular" etiqueta="Resultado del titular" valor={value.titular} opciones={OPC_TITULAR} disabled={disabled}
              onChange={(v) => set({ titular: v })} />
          </div>
          {tieneAval && (
            <div className="cm-field">
              <label>Aval{avalNombre ? ` · ${avalNombre}` : ''}</label>
              <Opciones nombre="resultado-aval" etiqueta="Resultado del aval" valor={value.aval} opciones={OPC_AVAL} disabled={disabled}
                onChange={(v) => set({ aval: v })} />
            </div>
          )}
          {cobra && (
            <div className="cm-field">
              <label>Monto pendiente que se sigue cobrando</label>
              <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00" disabled={disabled}
                aria-label="Monto pendiente"
                value={textoMonto(value, faltante)}
                onChange={(e) => set({ monto: e.target.value })} />
              {parseMonto(textoMonto(value, faltante)) > 0 && (
                <div className="cm-note">= {money(parseMonto(textoMonto(value, faltante)))}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Cambia, sobre una recuperación ya registrada, cómo se reporta a Buró: forma,
 * valor del auto o precio de venta, y qué pasa con el titular y el aval.
 * No toca la cartera. `cierre` = bloque de cierre del crédito (resumen.cierre).
 * onDone(cierreNuevo) se llama al guardar.
 */
export const CambiarResultadoBuroModal = ({ credito, cierre, onClose, onDone }) => {
  const c = credito || {};
  const k = cierre || {};
  const tieneAval = !!k.tiene_aval && (k.aval_con_resultado ?? c.aval_con_resultado) !== false;
  const deuda = k.deuda != null ? Number(k.deuda) : null;

  const [forma, setForma] = useState(k.forma === 'juicio' ? 'juicio' : 'entrega');
  const [valorAuto, setValorAuto] = useState(k.valor_auto != null ? Number(k.valor_auto).toFixed(2) : '');
  const [resultado, setResultado] = useState(() => eleccionDeCierre(k.resultado_buro));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const valorNum = parseMonto(valorAuto);
  // Con el valor nuevo se recalcula el faltante igual que el back (deuda − valor).
  const faltante = deuda != null
    ? Math.max(0, Math.round((deuda - (valorNum ?? 0)) * 100) / 100)
    : (k.faltante != null ? Number(k.faltante) : null);

  // Al cambiar el valor el faltante se mueve. Si el monto era el que se propone
  // (igual al faltante de antes) se queda siguiendo al faltante nuevo; si el
  // usuario lo escribió a mano, se respeta. monto null = sigue al faltante.
  function cambiarValor(texto) {
    setValorAuto(texto);
    setResultado((r) => {
      const m = r.monto != null ? parseMonto(r.monto) : null;
      const eraElPropuesto = m != null && faltante != null && Math.abs(m - faltante) < 0.005;
      return eraElPropuesto ? { ...r, monto: null } : r;
    });
  }

  async function guardar() {
    if (valorAuto.trim() !== '' && !(valorNum > 0)) {
      setError('El valor del auto debe ser mayor a cero (o déjalo vacío).');
      return;
    }
    const msg = validarResultadoBuro(resultado, tieneAval, faltante);
    if (msg) { setError(msg); return; }
    const body = { forma, valor_auto: valorNum };
    const rb = armarResultadoBuro(resultado, tieneAval, faltante);
    if (rb) body.resultado_buro = rb;

    setSubmitting(true);
    setError(null);
    try {
      const res = await carteraApi.recuperacionBuro(c.id, body);
      if (onDone) onDone(res?.data?.data?.cierre ?? null);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo guardar el resultado para Buró.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalPortal>
      <div className="cart-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
        <div className="cart-modal">
          <div className="cm-head">
            <h3>Cambiar resultado para Buró</h3>
            <button className="cm-close" onClick={onClose}><i className="pi pi-times" /></button>
          </div>
          <div className="cm-credit">
            <b>{c.cliente || '—'}</b> · {c.clave_contrato}
          </div>
          {error && <div className="cm-error">{error}</div>}

          {!k.resultado_buro && (
            <div className="cm-note" style={{ marginBottom: 10 }}>
              Esta recuperación no tiene resultado guardado para Buró. Al guardar se registra el que elijas aquí.
            </div>
          )}

          <div className="cm-field">
            <label>¿Cómo se recuperó?</label>
            <Opciones nombre="forma-recuperacion" etiqueta="Cómo se recuperó" valor={forma} opciones={FORMAS} disabled={submitting} onChange={setForma} />
          </div>

          <div className="cm-field">
            <label>Valor del auto / precio de venta</label>
            <input className="cm-input" type="text" inputMode="decimal" placeholder="0.00" disabled={submitting}
              aria-label="Valor del auto o precio de venta"
              value={valorAuto} onChange={(e) => cambiarValor(e.target.value)} />
            {valorNum != null && valorNum > 0
              ? <div className="cm-note">= {money(valorNum)}</div>
              : <div className="cm-note">Usa el precio de venta si ya se vendió; si no, el avalúo.</div>}
          </div>

          <ResultadoBuroCampos
            deuda={deuda}
            valorAuto={valorNum}
            faltante={faltante}
            tieneAval={tieneAval}
            avalNombre={k.aval_nombre}
            value={resultado}
            onChange={setResultado}
            disabled={submitting}
          />

          <div className="cm-actions">
            <button className="btn btn-sm" onClick={onClose} disabled={submitting}>Cancelar</button>
            <button className="btn btn-green btn-sm" onClick={guardar} disabled={submitting}>
              <i className="pi pi-check" />{submitting ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default CambiarResultadoBuroModal;
